import { BlueprintQuoteScope } from '@monetize/types/app';
import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import { useEffect, useMemo, useState } from 'react';
import { useGetBlueprintQuotes } from '../../../../api/cpqService';
import {
  MBox,
  MPageContainer,
  MSettingsPageHeader,
  MText,
  MVStack,
} from '../../../../components/Monetize';
import { BLUEPRINT_ADMIN_ROLES } from '../../../../services/acl/roles';
import { useAuth } from '../../../../services/auth0';
import { useDocumentHead } from '../../../../services/documentHead';
import { BlueprintModal } from '../../../Quotes/Quote/BlueprintModal';
import { BlueprintTable } from './components/BlueprintTable';

export const QuoteBluePrintsPage = () => {
  const { setDocTitle } = useDocumentHead();
  const { currentTenantUserHasRole } = useAuth();
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string | null>(
    null,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const isAdmin = currentTenantUserHasRole(BLUEPRINT_ADMIN_ROLES);

  const [pager, setPager] = useState<TDataTablePager>({
    first: DEFAULT_PAGER.first,
    rows: DEFAULT_PAGER.rows,
    page: DEFAULT_PAGER.page,
    sortField: DEFAULT_PAGER.sortField,
    sortOrder: DEFAULT_PAGER.sortOrder,
  });

  const filters = useMemo(() => {
    if (!isAdmin) {
      return { scope: BlueprintQuoteScope.USER };
    }
    return undefined;
  }, [isAdmin]);

  const { data: blueprintsData, isLoading: isBlueprintsLoading } =
    useGetBlueprintQuotes({
      config: pager,
      filters,
    });

  const handleEditBlueprint = (blueprintId: string) => {
    setSelectedBlueprintId(blueprintId);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedBlueprintId(null);
  };

  useEffect(() => {
    setDocTitle('Settings', 'Quote Blueprints');
  }, [setDocTitle]);

  return (
    <MPageContainer alignItems="stretch" data-testid="quote-blueprints-page">
      <MBox>
        <MSettingsPageHeader divider title="Quote Blueprints" />
      </MBox>

      <MBox w="full">
        <MVStack gap={4} maxW="600px" alignItems="flex-start">
          <MText>
            Quote Blueprints are pre-configured quotes as starting points for
            new quotes.
          </MText>
        </MVStack>

        <MBox mt={6}>
          <BlueprintTable
            data={blueprintsData?.content || []}
            totalElements={blueprintsData?.totalElements}
            isLoading={isBlueprintsLoading}
            onEdit={handleEditBlueprint}
            pager={pager}
            setPager={setPager}
          />
        </MBox>
      </MBox>

      {isEditModalOpen && selectedBlueprintId && (
        <BlueprintModal
          hideManageBlueprintsLink
          blueprintId={selectedBlueprintId}
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </MPageContainer>
  );
};

export default QuoteBluePrintsPage;
